import api from './api';

// Types
export interface POI {
  id: string; // UUID identifier (returned by backend for all API operations)
  name: string;
  latitude: string;
  longitude: string;
  category?: string;
  city?: string;
  country?: string;
  validation_status: 'PENDING' | 'VALIDATED' | 'REJECTED';
  created_by?: number; // Optional - only present for pending POIs created by user
  created_at: string;
  updated_at: string;
}

export interface Language {
  id: number;
  code: string;
  name: string;
  native_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface City {
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  poi_count: number;
}

export interface CitiesResponse {
  success: boolean;
  message: string;
  data: City[];
  filters: {
    min_pois_per_city: number;
  };
  performance: {
    strategy: string;
    cached: boolean;
  };
}

export interface Card {
  id: number; // Integer ID (required for admin operations)
  id_v2: string; // UUID identifier (preferred for card operations)
  poi_id: number;
  category: string;
  title?: string;
  subtitle?: string;
  text?: string;
  audio_url?: string;
  video_url?: string;
  image_url?: string;
  validation_status: 'PENDING' | 'VALIDATED' | 'REJECTED';
  user_id: string; // User UUID (string) - backend returns creator_uuid as user_id
  language_id?: number;
  created_at: string;
  updated_at: string;
  // Creator information (returned by backend joins)
  creator_first_name?: string;
  creator_last_name?: string;
  creator_display_name?: string;
  creator_email?: string;
  // POI information (returned by backend joins)
  poi_name?: string;
  latitude?: string;
  longitude?: string;
  // Language information (returned by backend joins)
  language_code?: string;
  language_name?: string;
  language_native_name?: string;
}

export interface POIResponse {
  success: boolean;
  message: string;
  data: POI[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  filters: {
    location: any;
  };
}

export interface CardResponse {
  success: boolean;
  message: string;
  data: Card[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface POIQueryParams {
  latitude?: number;
  longitude?: number;
  radius?: number; // Radius in meters
  city?: string; // City name for filtering
  limit?: number;
  offset?: number;
}

// Create POI request interface
export interface CreatePOIRequest {
  name: string;
  latitude: number;
  longitude: number;
  category?: string;
  city?: string;
  country?: string;
  google_place_id?: string;
}

// Nearby Google Place interface
export interface NearbyGooglePlace {
  placeId: string;
  name: string;
  latitude: number;
  longitude: number;
  types: string[];
  vicinity?: string;
}

// Nearby places search response interface
export interface NearbyPlacesResponse {
  success: boolean;
  message: string;
  data: NearbyGooglePlace[];
  metadata: {
    configured: boolean;
    search?: {
      latitude: number;
      longitude: number;
      radius: number;
    };
  };
}

// Update POI request interface
export interface UpdatePOIRequest {
  name: string;
  latitude: number;
  longitude: number;
  category?: string;
  city?: string;
  country?: string;
  google_place_id?: string;
  google_photo_reference?: string;
  description?: string;
}

// Create Card request interface
export interface CreateCardRequest {
  poi_id: string; // POI UUID (min 32 chars)
  category?: string;
  language_code?: string;
  title?: string;
  subtitle?: string;
  text?: string;
  audio_url?: string;
  video_url?: string;
  image_url?: string;
}

// POI Service Functions
export const poiService = {
  // Fetch all POIs with optional filters
  async fetchPOIs(params?: POIQueryParams): Promise<POIResponse> {
    try {
      const response = await api.post('/pois/search', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching POIs:', error);
      throw new Error('Failed to fetch POIs');
    }
  },

  // Fetch a single POI by ID
  async fetchPOIById(id: string): Promise<{ success: boolean; message: string; data: POI }> {
    try {
      const response = await api.get(`/pois/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching POI ${id}:`, error);
      throw new Error(`Failed to fetch POI ${id}`);
    }
  },

  // Fetch cards for a specific POI
  async fetchCardsByPOI(poiId: string, languageCode?: string): Promise<CardResponse> {
    try {
      const params = new URLSearchParams({ poi_id: poiId });
      if (languageCode) {
        params.append('language_code', languageCode);
      }
      const response = await api.get(`/cards?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cards for POI ${poiId}:`, error);
      throw new Error(`Failed to fetch cards for POI ${poiId}`);
    }
  },

  // Fetch a single card by ID_V2 (UUID) (returns original data, no translation)
  async fetchCardById(id_v2: string): Promise<{ success: boolean; message: string; data: Card }> {
    try {
      const response = await api.get(`/cards/${id_v2}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching card ${id_v2}:`, error);
      throw new Error(`Failed to fetch card ${id_v2}`);
    }
  },

  // Fetch POIs within a specific radius of coordinates (radius in meters, defaults to 10km)
  async fetchNearbyPOIs(latitude: number, longitude: number, radius: number = 10000): Promise<POIResponse> {
    try {
      const response = await api.post('/pois/search', { latitude, longitude, radius });
      return response.data;
    } catch (error) {
      console.error('Error fetching nearby POIs:', error);
      throw new Error('Failed to fetch nearby POIs');
    }
  },

  // Create a new POI
  async createPOI(data: CreatePOIRequest): Promise<{ success: boolean; message: string; data: POI }> {
    try {
      const response = await api.post('/pois', data);
      return response.data;
    } catch (error) {
      console.error('Error creating POI:', error);
      throw new Error('Failed to create POI');
    }
  },

  // Update a POI using UUID
  async updatePOI(id: string, data: UpdatePOIRequest): Promise<{ success: boolean; message: string; data: POI }> {
    try {
      const response = await api.put(`/pois/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating POI:', error);
      throw new Error('Failed to update POI');
    }
  },

  // Create a new Card
  async createCard(data: CreateCardRequest): Promise<{ success: boolean; message: string; data: Card }> {
    try {
      const response = await api.post('/cards', data);
      return response.data;
    } catch (error) {
      console.error('Error creating Card:', error);
      throw new Error('Failed to create Card');
    }
  },

  // Update a Card using ID_V2 (UUID)
  async updateCard(id_v2: string, data: Partial<CreateCardRequest>): Promise<{ success: boolean; message: string; data: Card }> {
    try {
      const response = await api.put(`/cards/${id_v2}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating Card:', error);
      throw new Error('Failed to update Card');
    }
  },

  // Delete a Card using ID_V2 (UUID)
  async deleteCard(id_v2: string): Promise<{ success: boolean; message: string; data: { deleted_card_id_v2: string } }> {
    try {
      const response = await api.delete(`/cards/${id_v2}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting Card:', error);
      throw new Error('Failed to delete Card');
    }
  },

  // Fetch cities with minimum POI count
  async fetchCities(): Promise<CitiesResponse> {
    try {
      const response = await api.get('/pois/cities');
      return response.data;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw new Error('Failed to fetch cities');
    }
  },

  // Fetch random POIs
  async fetchRandomPOIs(count: number = 5): Promise<POIResponse> {
    try {
      const response = await api.post('/pois/random', { count });
      return response.data;
    } catch (error) {
      console.error('Error fetching random POIs:', error);
      throw new Error('Failed to fetch random POIs');
    }
  },

  // Search for nearby Google Places by coordinates
  async searchNearbyGooglePlaces(latitude: number, longitude: number, radius: number = 100): Promise<NearbyPlacesResponse> {
    try {
      const response = await api.get('/pois/search-nearby-places', {
        params: { latitude, longitude, radius }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching nearby Google Places:', error);
      throw new Error('Failed to search nearby Google Places');
    }
  },

  // Save card order for a POI
  async saveCardOrder(poi_id: string, ordered_card_ids: string[]): Promise<{ success: boolean; message: string; data: { poi_id: string; card_count: number } }> {
    try {
      const response = await api.post('/cards/order', { poi_id, ordered_card_ids });
      return response.data;
    } catch (error) {
      console.error('Error saving card order:', error);
      throw new Error('Failed to save card order');
    }
  },

  // Fetch saved card order for a POI
  async fetchCardOrder(poi_id: string): Promise<{ success: boolean; message: string; data: { poi_id: string; ordered_card_ids: string[] } }> {
    try {
      const response = await api.get('/cards/order', {
        params: { poi_id }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching card order:', error);
      throw new Error('Failed to fetch card order');
    }
  }
};

export default poiService;
